// SearchModalContext.js
import axios from "axios";
import React, { createContext, useState, useContext, useEffect, useRef } from "react";

const SearchModalContext = createContext();

export const useSearchModal = () => useContext(SearchModalContext);

export const SearchModalProvider = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);

  const [searchedProducts, setSearchedProducts] = useState([]);
  const [searchFilter, setSearchFilter] = useState({
    page: 1,
    limit: 12,
    totalRecords: null,
  });

  // In-flight request tracker — protects against late responses overwriting
  // newer ones when the user keeps typing.
  const requestIdRef = useRef(0);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const searchProductsFunc = async (overrideQuery, overrideFilter) => {
    const q = (overrideQuery !== undefined ? overrideQuery : searchQuery)?.trim();
    if (!q) {
      setSearchedProducts([]);
      setSearchError(false);
      setSearchLoading(false);
      return;
    }

    const filterToUse = overrideFilter || searchFilter;
    const reqId = ++requestIdRef.current;

    setSearchLoading(true);
    if (searchError) setSearchError(false);

    try {
      const res = await axios.get("get-products", {
        params: { title: q, page: filterToUse.page, limit: filterToUse.limit },
      });

      // Drop response if a newer request fired in the meantime.
      if (reqId !== requestIdRef.current) return;

      const data = res?.data?.data?.map((el) => ({ ...el, id: el._id })) || [];
      setSearchedProducts(data);
      setSearchFilter((prev) => ({
        ...prev,
        totalRecords: res?.data?.totalRecords ?? data.length,
      }));
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      setSearchedProducts([]);
      setSearchError(true);
      setSearchFilter((prev) => ({ ...prev, totalRecords: 0 }));
    } finally {
      if (reqId === requestIdRef.current) setSearchLoading(false);
    }
  };

  const handleSearchPageChange = (event, value) => {
    setSearchFilter((prev) => ({ ...prev, page: value }));
  };

  // Reset everything when query is cleared
  useEffect(() => {
    if (searchQuery.length === 0) {
      setSearchedProducts([]);
      setSearchFilter((prev) => ({ ...prev, page: 1, totalRecords: null }));
      setSearchLoading(false);
      setSearchError(false);
    } else {
      // Reset to page 1 whenever the user changes the query.
      setSearchFilter((prev) =>
        prev.page === 1 ? prev : { ...prev, page: 1 }
      );
    }
  }, [searchQuery]);

  // Debounced auto-search — fires 350ms after the user stops typing.
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) return;
    const t = setTimeout(() => {
      searchProductsFunc(q);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Page change → refetch with the new page (skip page 1 because the query
  // change already fetched it via the debounce).
  useEffect(() => {
    if (searchFilter.page > 1 && searchQuery.trim()) {
      searchProductsFunc();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilter.page]);

  const valueData = {
    isModalOpen,
    setIsModalOpen,
    searchQuery,
    setSearchQuery,
    searchLoading,
    setSearchLoading,
    searchError,
    setSearchError,
    searchedProducts,
    setSearchedProducts,
    openModal,
    closeModal,
    searchProductsFunc,
    searchFilter,
    setSearchFilter,
    handleSearchPageChange,
  };

  return (
    <SearchModalContext.Provider value={valueData}>
      {children}
    </SearchModalContext.Provider>
  );
};
